import { AlertTriangle, FileWarning, Plus, PencilLine, Check } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { resyncFeldLabel, type RoutineResyncResponse } from '@/hooks/useRoutines';

/**
 * Was beim Neu-Einlesen der Routinen herauskam.
 *
 * Der Dialog ist der eigentliche Zweck des Knopfes: "eingelesen" allein sagt
 * niemandem, ob SEINE Änderung angekommen ist. Deshalb stehen hier die neu
 * angelegten Routinen, die geänderten MIT den betroffenen Feldern (alt → neu)
 * und vor allem die Dateien, die gar nicht gelesen werden konnten — mit
 * Dateiname und Fehlertext. Bis 10.09.2026 stand dieser Text nur im
 * Server-Log, wo ihn im Betrieb niemand sieht.
 *
 * Die unveränderten Routinen werden nur gezählt. Wer neun Zeilen „unverändert"
 * liest, überliest die eine Fehlermeldung darunter.
 */
export function ResyncErgebnisDialog({
  ergebnis,
  onClose,
}: {
  ergebnis: RoutineResyncResponse | null;
  onClose: () => void;
}): JSX.Element {
  const fehler = ergebnis?.fehler ?? [];
  const warnungen = ergebnis?.warnungen ?? [];
  const geaendert = ergebnis?.geaendert ?? [];
  const neu = ergebnis?.neu ?? [];

  return (
    <Dialog
      open={ergebnis !== null}
      onClose={onClose}
      title="Routinen neu eingelesen"
      // exactOptionalPropertyTypes: description darf nicht undefined sein,
      // also gar nicht erst gesetzt werden, solange kein Ergebnis vorliegt.
      {...(ergebnis
        ? { description: `${ergebnis.gelesen} Playbook(s) gelesen aus ${ergebnis.verzeichnis}` }
        : {})}
      footer={
        <Button variant="secondary" onClick={onClose} data-testid="resync-dialog-schliessen">
          Schließen
        </Button>
      }
    >
      {ergebnis && (
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          <div className="flex flex-wrap gap-2 text-sm" data-testid="resync-zusammenfassung">
            <Badge tone={neu.length > 0 ? 'primary' : 'neutral'}>
              <span className="inline-flex items-center gap-1">
                <Plus className="size-3" aria-hidden="true" />
                {neu.length} neu
              </span>
            </Badge>
            <Badge tone={geaendert.length > 0 ? 'primary' : 'neutral'}>
              <span className="inline-flex items-center gap-1">
                <PencilLine className="size-3" aria-hidden="true" />
                {geaendert.length} geändert
              </span>
            </Badge>
            <Badge tone="neutral">
              <span className="inline-flex items-center gap-1">
                <Check className="size-3" aria-hidden="true" />
                {ergebnis.unveraendert.length} unverändert
              </span>
            </Badge>
            <Badge tone={fehler.length > 0 ? 'danger' : 'neutral'}>
              <span className="inline-flex items-center gap-1">
                <FileWarning className="size-3" aria-hidden="true" />
                {fehler.length} Fehler
              </span>
            </Badge>
          </div>

          {ergebnis.verzeichnis_fehlt && (
            <p className="rounded-md border border-danger/40 bg-danger/10 p-2 text-sm">
              Der Routinen-Ordner <code className="font-mono text-xs">{ergebnis.verzeichnis}</code>{' '}
              existiert nicht. Ohne ihn kann nichts eingelesen werden — Clone anlegen und{' '}
              <code className="font-mono text-xs">ROUTINES_PATH</code> prüfen.
            </p>
          )}

          {fehler.length > 0 && (
            <section data-testid="resync-fehler">
              <h3 className="mb-1 text-sm font-semibold text-danger">Nicht eingelesene Dateien</h3>
              <ul className="flex flex-col gap-2">
                {fehler.map((f) => (
                  <li
                    key={f.datei}
                    className="rounded-md border border-danger/40 bg-danger/5 p-2 text-sm"
                  >
                    <div className="font-mono text-xs font-semibold">{f.datei}</div>
                    <div className="text-xs text-muted">{f.fehler}</div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {warnungen.length > 0 && (
            <section data-testid="resync-warnungen">
              <h3 className="mb-1 flex items-center gap-1 text-sm font-semibold text-warning">
                <AlertTriangle className="size-4" aria-hidden="true" />
                Hinweise
              </h3>
              <ul className="flex flex-col gap-1 text-xs text-muted">
                {warnungen.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </section>
          )}

          {neu.length > 0 && (
            <section data-testid="resync-neu">
              <h3 className="mb-1 text-sm font-semibold">Neu angelegt</h3>
              <ul className="flex flex-col gap-0.5 font-mono text-xs">
                {neu.map((slug) => (
                  <li key={slug}>{slug}</li>
                ))}
              </ul>
            </section>
          )}

          {geaendert.length > 0 && (
            <section data-testid="resync-geaendert">
              <h3 className="mb-1 text-sm font-semibold">Geändert</h3>
              <ul className="flex flex-col gap-2">
                {geaendert.map((g) => (
                  <li key={g.slug} className="rounded-md border border-border p-2">
                    <div className="font-mono text-xs font-semibold">{g.slug}</div>
                    <ul className="mt-1 flex flex-col gap-0.5 text-xs">
                      {g.aenderungen.map((a) => (
                        <li key={a.feld}>
                          <span className="font-medium">{resyncFeldLabel(a.feld)}</span>{' '}
                          <span className="text-muted">
                            {a.alt ?? '(leer)'} → {a.neu ?? '(leer)'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {ergebnis.gelesen === 0 && !ergebnis.verzeichnis_fehlt && fehler.length === 0 && (
            <p className="text-sm text-muted">
              Im Ordner liegt kein Playbook mit{' '}
              <code className="font-mono text-xs">wartungstool-meta</code>
              -Header.
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
